import React, { useState, useRef, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Button,
    Menu,
    MenuItem,
    Box,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';

const NavBar = ({ context }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState({});
    const [activeMenu, setActiveMenu] = useState(null);
    const menuRefs = {
        documents: useRef(null),
        research: useRef(null),
        about: useRef(null),
        signIn: useRef(null)
    };

    // Handle menu open
    const handleMenuOpen = (menuName, event) => {
        setAnchorEl({ ...anchorEl, [menuName]: event.currentTarget });
        setActiveMenu(menuName);
    };

    // Handle menu close
    const handleMenuClose = (menuName) => {
        setAnchorEl({ ...anchorEl, [menuName]: null });
        setActiveMenu(null);
    };

    // Handle key events for navigation
    const handleKeyDown = (event, menuName) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleMenuOpen(menuName, event);
        } else if (event.key === 'Escape' && activeMenu === menuName) {
            handleMenuClose(menuName);
        } else if (event.key === 'Tab' && activeMenu === menuName) {
            // Close menu when tabbing away
            handleMenuClose(menuName);
        }
    };

    // Handle arrow key navigation for dropdowns
    const handleMenuKeyDown = (event, menuName) => {
        if (event.key === 'Escape') {
            handleMenuClose(menuName);
            menuRefs[menuName]?.current?.focus();
        }
    };

    // Close all menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (Object.values(menuRefs).some(ref => ref.current && !ref.current.contains(event.target))) {
                Object.keys(anchorEl).forEach(key => {
                    if (anchorEl[key]) {
                        handleMenuClose(key);
                    }
                });
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [anchorEl]);

    // Navigation items
    const navItems = [
        {
            name: "Home",
            href: "/"
        },
        {
            name: 'Services',
            href: '/services'
        },
        {
            name: 'Documents',
            dropdown: [
                { name: 'Operational Datasets', href: '/documents/operational-datasets' },
                { name: 'Supported Formats', href: '/documents/supported-formats' },
                { name: 'Software Engineering', href: '/documents/software-engineering' },
                { name: 'Data Wrangling', href: '/documents/data-wrangling' },
                { name: 'Video Tutorials', href: '/documents/video-tutorials' }
            ]
        },
        {
            name: 'Research',
            dropdown: [
                { name: 'Peer-Reviewed Manuscripts', href: '/research/papers' },
                { name: 'Data Products', href: '/research/data-products' },
                { name: "Disserations and Theses", href: '/research/dissertations-and-theses' },
                { name: 'Abstracts', href: '/research/abstracts' },
                { name: 'How to Cite Sustain', href: '/research/cite-sustain' }
            ]
        },
        {
            name: 'About',
            dropdown: [
                { name: 'About', href: '/about' },
                { name: 'Our Team', href: '/about/our-team' },
                { name: 'Announcments', href: '/about/announcements' }
            ]
        }
    ];

    return (
        <AppBar position="static" sx={{ marginBottom: "0", backgroundColor: "#1653d9" }}>
            <Toolbar>
                {/* Logo or site name */}
                <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    Sustain
                </Typography>

                {/* Mobile menu button */}
                {isMobile && (
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        sx={{ width: "20vw", marginRight: "-5%" }}
                    >
                        <MenuIcon />
                    </IconButton>
                )}


                {/* Desktop Navigation */}
                {!isMobile && (
                    <Box sx={{ display: 'flex' }}>
                        {navItems.map((item) => (
                            <Box key={item.name} sx={{ position: 'relative' }}>
                                {item.dropdown ? (
                                    <>
                                        <Button
                                            ref={menuRefs[item.name.toLowerCase()]}
                                            color="inherit"
                                            aria-expanded={anchorEl[item.name.toLowerCase()] ? 'true' : undefined}
                                            aria-controls={`${item.name.toLowerCase()}-menu`}
                                            onClick={(e) => handleMenuOpen(item.name.toLowerCase(), e)}
                                            onKeyDown={(e) => handleKeyDown(e, item.name.toLowerCase())}
                                            sx={{ mx: 1 }}
                                        >
                                            {item.name}
                                        </Button>
                                        <Menu
                                            id={`${item.name.toLowerCase()}-menu`}
                                            anchorEl={anchorEl[item.name.toLowerCase()]}
                                            open={Boolean(anchorEl[item.name.toLowerCase()])}
                                            onClose={() => handleMenuClose(item.name.toLowerCase())}
                                            onKeyDown={(e) => handleMenuKeyDown(e, item.name.toLowerCase())}
                                            MenuListProps={{
                                                'aria-labelledby': `${item.name.toLowerCase()}-button`,
                                                role: 'menu'
                                            }}
                                        >
                                            {item.dropdown.map((subItem) => (
                                                <MenuItem
                                                    key={subItem.name}
                                                    onClick={() => handleMenuClose(item.name.toLowerCase())}
                                                    component="a"
                                                    href={subItem.href}
                                                >
                                                    {subItem.name}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </>
                                ) : (
                                    <Button
                                        color="inherit"
                                        href={item.href}
                                        sx={{ mx: 1 }}
                                    >
                                        {item.name}
                                    </Button>
                                )}
                            </Box>
                        ))}

                        {/* Sign In Button */}
                        <Button
                            ref={menuRefs.signIn}
                            color="inherit"
                            aria-expanded={anchorEl.signIn ? 'true' : undefined}
                            aria-controls="signin-menu"
                            onClick={() => context.setOpenModal(!context.openModal)}
                            onKeyDown={(e) => e.key === "Enter" && context.setOpenModal(!context.openModal)}
                            startIcon={<AccountCircle />}
                            sx={{ mx: 1 }}
                        >
                            Sign In
                        </Button>
                    </Box>
                )}

                {/* Mobile Navigation */}
                {isMobile && mobileMenuOpen && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '55px',
                            left: 0,
                            right: 0,
                            backgroundColor: 'background.paper',
                            zIndex: 1300,
                            p: 0
                        }}
                    >
                        {navItems.map((item) => (
                            <Box key={item.name} sx={{ mb: 1 }}>
                                {item.dropdown ? (
                                    <>
                                        <Button
                                            fullWidth
                                            color="inherit"
                                            aria-expanded={anchorEl[item.name.toLowerCase()] ? 'true' : undefined}
                                            aria-controls={`mobile-${item.name.toLowerCase()}-menu`}
                                            onClick={(e) => handleMenuOpen(item.name.toLowerCase(), e)}
                                            onKeyDown={(e) => handleKeyDown(e, item.name.toLowerCase())}
                                            sx={{ justifyContent: 'flex-start', color: 'text.primary' }}
                                        >
                                            {item.name}
                                        </Button>
                                        <Menu
                                            id={`mobile-${item.name.toLowerCase()}-menu`}
                                            anchorEl={anchorEl[item.name.toLowerCase()]}
                                            open={Boolean(anchorEl[item.name.toLowerCase()])}
                                            onClose={() => handleMenuClose(item.name.toLowerCase())}
                                            onKeyDown={(e) => handleMenuKeyDown(e, item.name.toLowerCase())}
                                            MenuListProps={{
                                                'aria-labelledby': `mobile-${item.name.toLowerCase()}-button`,
                                                role: 'menu'
                                            }}
                                        >
                                            {item.dropdown.map((subItem) => (
                                                <MenuItem
                                                    key={subItem.name}
                                                    onClick={() => handleMenuClose(item.name.toLowerCase())}
                                                    component="a"
                                                    href={subItem.href}
                                                >
                                                    {subItem.name}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </>
                                ) : (
                                    <Button
                                        fullWidth
                                        color="inherit"
                                        href={item.href}
                                        sx={{ justifyContent: 'flex-start', color: 'text.primary' }}
                                    >
                                        {item.name}
                                    </Button>
                                )}
                            </Box>
                        ))}
                        <Button
                            fullWidth
                            color="inherit"
                            aria-expanded={anchorEl.signIn ? 'true' : undefined}
                            aria-controls="mobile-signin-menu"
                            onClick={() => context.setOpenModal(!context.openModal)}
                            onKeyDown={(e) => e.key === "Enter" && context.setOpenModal(!context.openModal)}
                            startIcon={<AccountCircle />}
                            sx={{ justifyContent: 'flex-start', color: 'text.primary' }}
                        >
                            Sign In
                        </Button>
                    </Box>
                )}
            </Toolbar>
        </AppBar>
    );
};

export default NavBar;